import { nsXsf, nsXsf2, nsXsf3 } from '@qdabra/forms-shared/xmlNamespaces';
import { allChildEls } from 'func-xml';
import {
    both,
    filter,
    find,
    groupBy,
    indexBy,
    isEmpty,
    map,
    prop,
    propEq,
} from 'ramda';
import Rule from '../../Qd/formsviewer/Rules/rule';
import {
    AssignmentAction,
    ChangeAdapterAction,
    CloseDocumentAction,
    DialogExpressionAction,
    DialogMessageAction,
    ExitAction,
    QueryAction,
    SubmitAction,
    switchViewAction,
} from '../../Qd/formsviewer/Rules/ruleActions';
import RuleSet from '../../Qd/formsviewer/Rules/ruleSet';
import {
    buildNamespaceManagerFromMap,
    hasYesAttribute,
} from '../../Qd/utility/xmlHelpers';
import {
    getChildElements,
    getChildNodes,
    isProcessingInstruction,
} from '../../Qd/xmlUtility';
import { ContextMenuItem, XPathEngine } from '../formsviewer.domain';
import SharePointListAdapter from '../models/sharePointListAdapter';
import nodeContentComponent from './collectionEditableComponent';
import DefaultValue from './defaultValueFormula';
import {
    ACTION_COLLECTION_INSERT,
    ACTION_COLLECTION_INSERT_AFTER,
    ACTION_COLLECTION_INSERT_BEFORE,
    ACTION_COLLECTION_REMOVE,
    ACTION_INSERT,
    ACTION_INSERT_AFTER,
    ACTION_INSERT_BEFORE,
    ACTION_OPTIONAL_INSERT,
    ACTION_OPTIONAL_REMOVE,
    ACTION_REMOVE,
} from './editableComponentActions';
import {
    COLLECTION_COMPONENT_TYPE,
    FIELD_COMPONENT_TYPE,
    OPTIONAL_COMPONENT_TYPE,
    TEXT_LIST_COMPONENT_TYPE,
} from './editableComponentTypes';
import { parseWebServiceInput } from './manifest.parser.soapAdapter';
import RuleSetByDataSourceCollection from './ruleSetCollection';
import UpgradeSettings from './upgradeSettings';

var dataObjectPath = '/*/xsf:dataObjects/xsf:dataObject';

var nodeValOrNull = function (node) {
    return node ? node.textContent : null;
};

var getChildNode = function (parent, childName) {
    return find(propEq('localName', childName), parent.childNodes) || null;
};

const getActionCode = function (action) {
    switch (action) {
        case ACTION_COLLECTION_INSERT_BEFORE:
            return ACTION_INSERT_BEFORE;
        case ACTION_COLLECTION_INSERT_AFTER:
            return ACTION_INSERT_AFTER;
        case ACTION_COLLECTION_REMOVE:
        case ACTION_OPTIONAL_REMOVE:
            return ACTION_REMOVE;
        case ACTION_COLLECTION_INSERT:
        case ACTION_OPTIONAL_INSERT:
            return ACTION_INSERT;
        default:
            return null;
    }
};

const parseButtonAction = function (buttonNode) {
    const actionStr = buttonNode.getAttribute('action');

    return {
        type: getActionCode(actionStr),
        caption: buttonNode.getAttribute('caption'),
    };
};

/***********************************
 ***** Manifest Parser - constructor
 ***********************************/
function ManifestParser() {
    var xpathEngine = new XPathEngine();

    this.fieldRules = null;
    this.buttonRules = {};
    this.repeatingSections = {};
    this.defaultValues = null;
    this.views = null; // first entry is always default view
    this.contextMenuItemsByView = {};

    this.xpathEngine = xpathEngine;

    // (String, { context: DOMNode }?) -> XPathResult
    this.evaluateXPath = xpathEngine.evaluateXPath.bind(xpathEngine);

    var parseFieldComponent = function (componentType, editWithNode) {
        const maxLength = editWithNode.getAttribute('maxLength');

        return {
            component: componentType,
            maxLength: maxLength ? Number(maxLength) : null,
        };
    };

    const parseNodeContentComponent = function (xteNode, componentType) {
        let cc = nodeContentComponent(xpathEngine, componentType);

        const fragmentContainer = xpathEngine
            .evaluateXPath(
                'xsf:editWith/xsf:fragmentToInsert/xsf:chooseFragment',
                { context: xteNode },
            )
            .first();

        if (!fragmentContainer) {
            throw Error(
                'Unable to locate fragment container for editable component.',
            );
        }

        Object.assign(cc, {
            fragmentContainer,
            innerFragmentPath: fragmentContainer.getAttribute('innerFragment'),
            parent: fragmentContainer.getAttribute('parent') || '.',
        });

        // var fragSlashPos = cc.innerFragmentPath && innerFragmentPath.indexOf("/")
        // UNUSED? ec.fragmentName = (fragSlashPos !== -1) ? innerFragment.substring(fragSlashPos + 1) : innerFragment

        return cc;
    };

    var parseTextListComponent = function () {
        return {
            component: TEXT_LIST_COMPONENT_TYPE,
        };
    };

    var createTypedComponent = function (xteNode) {
        const editWithNode = xteNode.getElementsByTagNameNS(
            nsXsf,
            'editWith',
        )[0];
        const componentType = editWithNode.getAttribute('component');

        switch (componentType) {
            case FIELD_COMPONENT_TYPE:
                return parseFieldComponent(componentType, editWithNode);
            case COLLECTION_COMPONENT_TYPE:
            case OPTIONAL_COMPONENT_TYPE:
                return parseNodeContentComponent(xteNode, componentType);
            case TEXT_LIST_COMPONENT_TYPE:
                return parseTextListComponent(xteNode);
            default:
                console.warn('Unsupported component type: ' + componentType);
        }

        return null;
    };

    const getEditableComponentActions = function (viewNode, xmlToEditName) {
        // see if there are any repeating group controls for the current component
        const path = `xsf:menuArea/xsf:button[@xmlToEdit = '${xmlToEditName}']`;
        const buttonNodes = xpathEngine
            .evaluateXPath(path, { context: viewNode })
            .toUnsortedArray();

        return buttonNodes.map(parseButtonAction);
    };

    this.parseEditableComponent = function (xteNode, viewNode) {
        let component = createTypedComponent(xteNode);

        if (component) {
            const fullItem = xteNode.getAttribute('item');
            const lastSlashIndex = fullItem.lastIndexOf('/');
            const name = xteNode.getAttribute('name');

            Object.assign(component, {
                name,
                fullItem,
                container: xteNode.getAttribute('item'),
                viewContext: xteNode.getAttribute('viewContext'),
                item: fullItem.substring(0, lastSlashIndex),
                actions: getEditableComponentActions(viewNode, name),
            });

            if (component.component === TEXT_LIST_COMPONENT_TYPE) {
                component.element = fullItem.substring(lastSlashIndex + 1);
            }
        }

        return component;
    };
}

// viewNodes is Object[String -> DomNode] associating view names with their source nodes
ManifestParser.prototype.viewNodes = null;

// String -> ()
ManifestParser.prototype.setManifest = function (manifestDom) {
    // 2015-07-30 - We need to use specific namespaces that may not be declared in the manifest, so we create the namespace manager with these specific namespaces
    const nsr = buildNamespaceManagerFromMap({
        xsf: nsXsf,
        xsf2: nsXsf2,
        xsf3: nsXsf3,
    });

    this.manifestDOM = manifestDom;
    this.xpathEngine.baseNode = manifestDom;
    this.xpathEngine.nsr = nsr;
};

// String -> String
function getLocalName(fieldName) {
    if (!fieldName) {
        console.warn('getLocalName() called with blank field name.');

        return '';
    }

    var parts = fieldName.split(':');

    return parts.length === 1 ? parts[0] : parts[1];
}

/**********************************
 *** Get Field Rules
 **********************************/
// (String, String) -> DomHandler[]
ManifestParser.prototype.getFieldRuleCandidates = function (dom, fieldName) {
    var fieldRules = this.getFieldRules();
    var byDom = fieldRules.getByDsName(dom || '');
    var byField = byDom && byDom.getByFieldName(getLocalName(fieldName));

    return byField;
};

// () -> RuleSetByDataSourceCollection
ManifestParser.prototype.getFieldRules = function () {
    return this.lazyLoad(this, 'fieldRules', this.extractFieldRules.bind(this));
};

ManifestParser.prototype.lazyLoad = function (target, property, fLoad) {
    if (!target[property]) {
        target[property] = fLoad.call(this);
    }
    return target[property];
};

ManifestParser.prototype.getButtonRules = function (view) {
    return this.lazyLoad(
        this.buttonRules,
        view,
        this.extractViewButtonRules.bind(this, view),
    );
};

ManifestParser.prototype.getOnLoadRules = function () {
    return this.lazyLoad(this, 'onLoadRules', this.extractOnLoadRules);
};

ManifestParser.prototype.evaluateXPathToString = function (
    expression,
    options,
) {
    return this.evaluateXPath(expression, options).toString();
};

ManifestParser.prototype.evaluateBoolean = function (expression, options) {
    return this.evaluateXPath(expression, options).booleanValue();
};

ManifestParser.prototype.getMainSchema = function () {
    var schemaPath =
        "/*/xsf:documentSchemas/xsf:documentSchema[@rootSchema='yes']";
    var result = this.evaluateXPath(schemaPath);
    return result.first().getAttribute('location').split(' ')[1];
};

ManifestParser.prototype.getBaseUrl = function () {
    var xPath =
        '/*/xsf:extensions/xsf:extension/xsf3:solutionDefinition/xsf3:baseUrl/@relativeUrlBase';

    return this.evaluateXPathToString(xPath);
};

ManifestParser.prototype.getBasePath = function () {
    // Remove final part (e.g. template.xsn)
    var baseUrl = this.getBaseUrl();
    if (!baseUrl.match(/.xsn/i)) {
        return baseUrl;
    }

    return baseUrl.replace(/[^/]+$/, '');
};

ManifestParser.prototype.getPublishUrl = function () {
    return this.evaluateXPathToString('/*/@publishUrl');
};

ManifestParser.prototype.getViews = function () {
    if (!this.views) {
        this.views = this.parseViewNames();
    }

    return this.views;
};

ManifestParser.prototype.parseViewNames = function () {
    var views = [],
        viewsGroup = this.evaluateXPath('/*/xsf:views').first(),
        defaultView = viewsGroup.getAttribute('default'),
        viewNodes = this.evaluateXPath('xsf:view', {
            context: viewsGroup,
        }).toUnsortedArray();

    views.push(defaultView);

    // Populate array with non-default views
    viewNodes
        .map(function (node) {
            return node.getAttribute('name');
        })
        .filter(function (name) {
            return name !== defaultView;
        })
        .forEach(function (name) {
            views.push(name);
        });

    return views;
};

ManifestParser.prototype.getViewFilename = function (view) {
    var viewNode = this.getViewNode(view),
        result =
            viewNode &&
            this.evaluateXPath('xsf:mainpane', { context: viewNode }).first();

    return result && result.getAttribute('transform');
};

ManifestParser.prototype.getDefaultValues = function (field) {
    if (!this.defaultValues) {
        this.defaultValues = this.extractDefaultValues();
    }

    return this.defaultValues[field];
};

ManifestParser.prototype.getAllDefaultValues = function () {
    return this.getDefaultValues('#all');
};

ManifestParser.prototype.getAllRefDefaultValues = function () {
    return this.getDefaultValues('#allref');
};

ManifestParser.prototype.getEditableComponents = function (viewName) {
    var viewComponents = this.repeatingSections[viewName];

    if (!viewComponents) {
        viewComponents = this.extractEditableComponents(viewName);
        this.repeatingSections[viewName] = viewComponents;
    }

    return viewComponents;
};

ManifestParser.prototype.getEditableComponent = function (viewName, ecName) {
    var components = this.getEditableComponents(viewName);

    return components.find(propEq('name', ecName));
};

// String, String -> Array[ContextMenuItem]
ManifestParser.prototype.getContextMenuItems = function (viewName, xmlToEdit) {
    var contextItems = this.contextMenuItemsByView[viewName];

    if (!contextItems) {
        contextItems = this.extractContextMenuItems(viewName);
        this.contextMenuItemsByView[viewName] = contextItems;
    }

    return contextItems[xmlToEdit] || [];
};

ManifestParser.prototype.getUpgradeSettings = function () {
    var settings = new UpgradeSettings();
    var upgradeNode = this.xpathEngine
        .evaluateXPath('/*/xsf:documentVersionUpgrade/xsf:useTransform')
        .first();

    if (upgradeNode) {
        settings.type = 'transform';
        settings.transform = upgradeNode.getAttribute('transform');
        settings.minVersion = upgradeNode.getAttribute('minVersionToUpgrade');
        settings.maxVersion = upgradeNode.getAttribute('maxVersionToUpgrade');
    } else {
        settings.type = 'none';
    }

    return settings;
};

ManifestParser.prototype.getSolutionVersion = function () {
    return this.evaluateXPathToString('/*/@solutionVersion');
};

ManifestParser.prototype.extractDefaultValues = function () {
    var calcFields = this.evaluateXPath(
        '/*/xsf:calculations/xsf:calculatedField',
    );
    var allDefVals = calcFields
        .toUnsortedArray()
        .map(this.extractDefaultValue.bind(this));
    var allRefDefVals = filter(function (dv) {
        return dv.getRefresh() === 'onChange';
    }, allDefVals);

    return {
        '#all': allDefVals,
        '#allref': allRefDefVals,
    };
};

ManifestParser.prototype.extractDefaultValue = function (defaultValueNode) {
    var target = defaultValueNode.getAttribute('target'),
        expression = defaultValueNode.getAttribute('expression'),
        refresh = defaultValueNode.getAttribute('refresh');

    return new DefaultValue(target, expression, refresh);
};

// Map from rule action node names to the function for parsing them
ManifestParser.ruleParsers = {
    assignmentAction: function (node) {
        var targetField = node.getAttribute('targetField');
        var expression = node.getAttribute('expression');

        return new AssignmentAction(targetField, expression);
    },

    changeAdapterProperty: function (node) {
        var adapter = node.getAttribute('adapter');
        var expression = node.getAttribute('expression');
        var adapterProperty = node.getAttribute('adapterProperty');

        return new ChangeAdapterAction(adapter, expression, adapterProperty);
    },

    closeDocumentAction: function (node) {
        var promptSave = node.getAttribute('promptToSaveChanges');

        return new CloseDocumentAction(promptSave);
    },

    dialogBoxExpressionAction: function (node) {
        var expression = node.textContent;

        return new DialogExpressionAction(expression);
    },

    dialogBoxMessageAction: function (node) {
        var message = node.textContent;

        return new DialogMessageAction(message);
    },

    exitRuleSet: function () {
        return new ExitAction();
    },

    openNewDocumentAction: null, // TODO

    queryAction: function (node) {
        var adapter = node.getAttribute('adapter');

        return new QueryAction(adapter);
    },

    submitAction: function (node) {
        var adapter = node.getAttribute('adapter');
        return new SubmitAction(adapter);
    },

    switchViewAction: function (node) {
        var viewName = node.getAttribute('view');
        return switchViewAction(viewName);
    },
};

//========================
// Create Action (helper)
//========================
function getRuleSetActionNode(parent) {
    return parent.getElementsByTagNameNS(nsXsf, 'ruleSetAction')[0];
}

var createAction = function (actionNode) {
    var actionType = actionNode.localName;
    var parser = ManifestParser.ruleParsers[actionType];
    var supported = !!parser;

    //12-21-2017 Add functionality to return unsupported parser used in rule editor.
    return {
        action: supported ? parser(actionNode) : {},
        localName: actionNode.localName,
        actionNode,
        supported: supported,
    };
};

const ruleSetIdFromRuleSetReference = (ruleSetReferenceNode) =>
    ruleSetReferenceNode.getAttribute('ruleSet');

ManifestParser.prototype.extractDomHandler = function (
    domHandlerNode,
    ruleSets,
) {
    const ruleSetAction = getRuleSetActionNode(domHandlerNode);
    const ruleSetId = ruleSetIdFromRuleSetReference(ruleSetAction);

    const match = domHandlerNode.getAttribute('match');
    const dataObject = domHandlerNode.getAttribute('dataObject') || '';

    const ruleSet = ruleSets[ruleSetId];

    return {
        match,
        dataObject,
        ruleSet,
    };
};

const ruleElementToRule = function (ruleNode) {
    const ruleCondition = ruleNode.getAttribute('condition');
    const ruleCaption = ruleNode.getAttribute('caption');
    const ruleEnabled = ruleNode.getAttribute('isEnabled');
    // Treat rule as enabled if isEnabled attribute is absent
    const boolEnabled = !ruleEnabled || ruleEnabled === 'yes';

    // Create new rule object
    const newRule = new Rule(ruleCaption, ruleCondition, boolEnabled);

    return newRule;
};

const extractRuleFromNode = function (ruleNode) {
    let rule = ruleElementToRule(ruleNode);

    const actions = allChildEls(ruleNode).map(createAction).filter(Boolean);

    // Loop through all action nodes for current rule
    for (const act of actions) {
        rule.addAction(act);
    }

    return rule;
};

const parseRuleSet = function (ruleSetNode) {
    const name = ruleSetNode.getAttribute('name');
    const ruleNodes = ruleSetNode.getElementsByTagNameNS(nsXsf, 'rule');
    const ruleSet = new RuleSet(name);

    for (const node of Array.from(ruleNodes)) {
        ruleSet.addRule(extractRuleFromNode(node));
    }

    return ruleSet;
};

// DomNode(RuleSetAction) -> RuleSet
// Locates the rule definitions corresponding to the specified ruleset node, and returns
// a ruleSet containing those rules
ManifestParser.prototype.createRuleSet = function (ruleSetAction) {
    const path = `/*/xsf:ruleSets/xsf:ruleSet[@name = '${ruleSetIdFromRuleSetReference(
        ruleSetAction,
    )}']`;
    const ruleSetNode = this.evaluateXPath(path).first();

    return parseRuleSet(ruleSetNode);
};

ManifestParser.prototype.parseAllRuleSets = function () {
    const ruleSets = this.evaluateXPath('/*/xsf:ruleSets/xsf:ruleSet')
        .toUnsortedArray()
        .map(parseRuleSet);

    return indexBy(prop('name'), ruleSets);
};

ManifestParser.prototype.getAllRuleSets = function () {
    return this.lazyLoad(this, 'ruleSets', this.parseAllRuleSets.bind(this));
};

/***************************************
 ***** Get Field Rules
 ***************************************/
// () => RuleSetByDataSourceCollection
ManifestParser.prototype.extractFieldRules = function () {
    var self = this;

    try {
        const ruleSetIndex = new RuleSetByDataSourceCollection();
        const allRuleSets = self.getAllRuleSets();

        const domEventNodes = self
            .evaluateXPath(
                '/*/xsf:domEventHandlers/xsf:domEventHandler[xsf:ruleSetAction]',
            )
            .toUnsortedArray();

        for (const domEventNode of domEventNodes) {
            const domHandler = self.extractDomHandler(
                domEventNode,
                allRuleSets,
            );
            const fieldName = getLocalName(domHandler.match.split('/').pop());
            const byDsName = ruleSetIndex.ensureByFieldCollection(
                domHandler.dataObject,
            );
            let byField = byDsName.ensureRuleSetCollection(fieldName);

            byField.push(domHandler);
        }

        return ruleSetIndex;
    } catch (e) {
        throw new Error(
            'Error Getting Dom Event Handler Nodes: ' +
                (e.message ? e.message : e),
        );
    }
};

ManifestParser.prototype.extractViewButtonRules = function (viewName) {
    let buttonRules = {};
    const path = `/*/xsf:views/xsf:view[@name = '${viewName}']/xsf:unboundControls/xsf:button[xsf:ruleSetAction]`;

    try {
        const buttonNodes = this.evaluateXPath(path).toUnsortedArray();

        if (!isEmpty(buttonNodes)) {
            const ruleSets = this.getAllRuleSets();

            for (const buttonNode of buttonNodes) {
                const ctrlId = buttonNode.getAttribute('name');

                buttonRules[ctrlId] = this.extractButtonRules(
                    buttonNode,
                    ruleSets,
                );
            }
        }
    } catch (e) {
        alert('Error Getting Button Nodes: ' + (e.message || e));
    }

    return buttonRules;
};

ManifestParser.prototype.extractButtonRules = function (buttonNode, ruleSets) {
    const ruleSetAction = getRuleSetActionNode(buttonNode);
    const ruleSetId = ruleSetIdFromRuleSetReference(ruleSetAction);

    return ruleSets[ruleSetId];
};

ManifestParser.prototype.extractOnLoadRules = function () {
    try {
        // extract all OnLoad nodes (should only be one)
        const onLoadNodes = this.evaluateXPath(
            '/*/xsf:onLoad/xsf:ruleSetAction',
        ).toUnsortedArray();

        if (!isEmpty(onLoadNodes)) {
            const ruleSets = this.getAllRuleSets();
            const ruleSetId = ruleSetIdFromRuleSetReference(onLoadNodes[0]);

            return ruleSets[ruleSetId];
        }
    } catch (e) {
        console.error('Encountered an error while parsing onLoad rules', e);
    }

    return null;
};

// 2014-04-18 Deleted debugging methods (printFieldRules, printOtherRules)

function adapterPathWithPredicate(predicatePath) {
    return (
        '(/*/xsf:submit | /*/xsf:dataAdapters | /*/xsf:dataObjects/xsf:dataObject/xsf:query | /*/xsf:query)/*[' +
        predicatePath +
        ']'
    );
}

var submitAdapterSelector =
    'self::xsf:davAdapter or self::xsf:webServiceAdapter';
var submitAdapterPath = adapterPathWithPredicate(submitAdapterSelector);

var allAdapterSelector =
    submitAdapterSelector +
    ' or self::xsf:sharepointListAdapterRW or self::xsf:xmlFileAdapter or self::xsf:adoAdapter';
var allAdapterPath = adapterPathWithPredicate(allAdapterSelector);

ManifestParser.SubmitAdapterPath = submitAdapterPath;

ManifestParser.prototype.getSubmitAdapter = function (name) {
    var match = this.getAdapterNode(submitAdapterPath, name);

    if (match) {
        return this.parseAdapter(match);
    }
    // ERROR
    return null;
};

ManifestParser.prototype.getDataSources = function () {
    if (!this.allDataSourcesLoaded) {
        this.dataSources = this.parseDataSources();
        this.allDataSourcesLoaded = true;
    }

    return this.dataSources;
};

ManifestParser.prototype.parseDataSources = function () {
    var dsNodes = this.evaluateXPath(
        dataObjectPath + '[xsf:query]',
    ).toUnsortedArray();
    var dses = dsNodes.map(this.parseDataSource.bind(this));

    return indexBy(prop('name'), dses);
};

ManifestParser.prototype.parseDataSource = function (dsNode) {
    const name = dsNode.getAttribute('name');
    const initOnLoad = hasYesAttribute('initOnLoad', dsNode);
    const queryNode = dsNode.getElementsByTagNameNS(nsXsf, 'query')[0];
    const adapterNode = queryNode && queryNode.firstElementChild;
    const adapter = adapterNode ? this.parseAdapter(adapterNode) : null;

    return {
        name,
        initOnLoad,
        adapter,
        schema: dsNode.getAttribute('schema'),
    };
};

function makeDataSourceDescriptor(node) {
    const name = node.getAttribute('name');
    const initOnLoad = hasYesAttribute('initOnLoad', node);

    return { name: name, initOnLoad: initOnLoad };
}

ManifestParser.prototype.listDataSources = function () {
    return this.evaluateXPath(dataObjectPath)
        .toUnsortedArray()
        .map(makeDataSourceDescriptor);
};

ManifestParser.prototype.makeUdcxConnectionObject = function (node) {
    const queryThisFormOnly = hasYesAttribute('queryThisFormOnly', node);
    const connectoidNodes = this.evaluateXPath('xsf2:connectoid', {
        context: node,
    }).toUnsortedArray();

    const udcxItem = {
        ref: node.getAttribute('ref'),
        queryThisFormOnly,
    };

    if (!connectoidNodes.length) {
        return udcxItem;
    }

    const connectoidNode = connectoidNodes[0];

    udcxItem['linkType'] = connectoidNode.getAttribute('connectionLinkType');
    udcxItem['source'] = connectoidNode.getAttribute('source');
    udcxItem['siteCollection'] = connectoidNode.getAttribute('siteCollection');

    return udcxItem;
};

ManifestParser.prototype.parseUdcxConnections = function () {
    // 2023-11-01 - Removed unused udcxConnTargetPath
    var udcxConnPath =
        '/*/xsf:extensions/xsf:extension/xsf2:solutionDefinition/xsf2:dataConnections/*[xsf2:connectoid]';

    return this.evaluateXPath(udcxConnPath)
        .toUnsortedArray()
        .map(this.makeUdcxConnectionObject.bind(this));
};

ManifestParser.prototype.getUdcxConnections = function () {
    if (!this.allUdcxLoaded) {
        this.udcxConnections = this.parseUdcxConnections();
        this.allUdcxLoaded = true;
    }

    return this.udcxConnections;
};

ManifestParser.prototype.getDataConnections = function () {
    if (!this.allDcsLoaded) {
        this.dataConnections = this.parseDataConnections();
        this.allDcsLoaded = true;
    }

    return this.dataConnections;
};

ManifestParser.prototype.parseDataConnections = function () {
    var dcNodes = this.evaluateXPath(allAdapterPath),
        dcs = dcNodes.toUnsortedArray().map(this.parseAdapter.bind(this)),
        dcMap = {};

    dcs.forEach(function (dc) {
        dcMap[dc.name] = dc;
    });

    return dcMap;
};

ManifestParser.prototype.parseDataConnectionExtensions = function () {
    var dcNodes = this.evaluateXPath(
        '/*/xsf:extensions/xsf:extension/xsf2:solutionDefinition/xsf2:dataConnections/*',
    );
    return dcNodes.toUnsortedArray().map(function (nodeItem) {
        return {
            type: nodeItem.localName,
            name: nodeItem.getAttribute('ref'),
            node: nodeItem,
        };
    });
};

ManifestParser.prototype.getDataConnectionExtensions = function () {
    if (!this.dataConnectionExtensionsLoaded) {
        this.dataConnectionExtensions = this.parseDataConnectionExtensions();
        this.dataConnectionExtensionsLoaded = true;
    }

    return this.dataConnectionExtensions;
};

ManifestParser.prototype.getDataConnectionExtension = function (type, name) {
    if (!type || !name) {
        return null;
    }

    var dataConnections = this.getDataConnectionExtensions();
    if (!dataConnections.length) {
        return null;
    }

    var filterConnection = dataConnections.filter(function (dc) {
        return dc.type === type + 'Extension' && dc.name === name;
    });

    if (!filterConnection.length) {
        return null;
    }

    return filterConnection[0].node;
};

ManifestParser.prototype.getQueryAdapter = function (name) {
    var dataSources = this.getDataSources(),
        source = dataSources[name],
        adapter = source && source.adapter;

    return adapter;
};

ManifestParser.prototype.parseAdapter = function (node) {
    var parser = this.getAdapterParser(node.localName);

    return parser?.call(this, node);
};

ManifestParser.prototype.getAdapterParser = function (nodeName) {
    switch (nodeName) {
        case 'adoAdapter':
            return this.parseAdoAdapter;
        case 'davAdapter':
            return this.parseDavAdapter;
        case 'sharepointListAdapterRW':
            return this.parseShPListAdapter;
        case 'webServiceAdapter':
            return this.parseWebServiceAdapter;
        case 'xmlFileAdapter':
            return this.parseFileQueryAdapter;
        default:
            throw new Error('Unsupported adapter type: ' + nodeName);
    }
};

ManifestParser.prototype.getAdapterNode = function (xpath, name) {
    var candidates, match;

    candidates = this.evaluateXPath(xpath).toUnsortedArray();
    match = candidates.filter(function (a) {
        return a.getAttribute('name') === name;
    })[0];

    return match;
};

ManifestParser.prototype.getSubmitSettings = function () {
    var submitNode = this.evaluateXPath('/*/xsf:submit').first();

    if (!submitNode) {
        // Not found
        return null;
    }

    var submitSettings = {};

    submitSettings.onAfterSubmit = submitNode.getAttribute('onAfterSubmit');
    submitSettings.showStatusDialog = hasYesAttribute(
        'showStatusDialog',
        submitNode,
    );
    submitSettings.errorMessage = nodeValOrNull(
        getChildNode(submitNode, 'errorMessage'),
    );

    var useQueryAdapterNode = this.evaluateXPath('xsf:useQueryAdapter', {
        context: submitNode,
    }).first();
    var adapterPathPrefix = useQueryAdapterNode ? '/*/xsf:query/' : '';
    var adapterPath = adapterPathPrefix + '*[' + allAdapterSelector + ']';
    var adapterNode = this.evaluateXPath(adapterPath, {
        context: submitNode,
    }).first();

    if (adapterNode) {
        submitSettings.adapter = adapterNode
            ? this.parseAdapter(adapterNode)
            : null;
    } else {
        var ruleSetNode = getRuleSetActionNode(submitNode);
        if (ruleSetNode) {
            submitSettings.ruleSet = this.createRuleSet(ruleSetNode);
        }
    }

    return submitSettings;
};

// ======================
// Adapter parsing
// ======================

ManifestParser.prototype.checkAndAddUdcxProperty = function (
    adapterNode,
    adapter,
) {
    var udcxConnections = this.getUdcxConnections(),
        adapterName = adapterNode.getAttribute('name'),
        udcxConnectionsFilter = udcxConnections.filter(function (conn) {
            return conn.ref === adapterName;
        });

    if (!udcxConnectionsFilter.length) {
        return adapter;
    }

    var udcxNode = udcxConnectionsFilter[0];
    adapter.source = udcxNode.source;
    adapter.siteCollection = udcxNode.siteCollection;
    adapter.type = 'udcx';

    return adapter;
};

ManifestParser.prototype.parseDavAdapter = function (adapterNode) {
    var adapter = this.parseAdapterCommon(adapterNode, 'dav');
    var folderUrlNode = getChildNode(adapterNode, 'folderURL');
    var fileNameNode = getChildNode(adapterNode, 'fileName');

    adapter.enabled = hasYesAttribute('submitAllowed', adapterNode);
    adapter.overwrite = hasYesAttribute('overwriteAllowed', adapterNode);
    adapter.folder = folderUrlNode.getAttribute('value');
    adapter.fileName = fileNameNode.getAttribute('value');
    adapter.fileNameType = fileNameNode.getAttribute('valueType');

    return adapter;
};

var parseShPListField = function (fieldDef) {
    return {
        internalName: fieldDef.getAttribute('internalName'),
        type: fieldDef.getAttribute('type'),
    };
};

ManifestParser.prototype.parseShPListAdapter = function (adapterNode) {
    const fieldNodes = adapterNode.getElementsByTagNameNS(nsXsf, 'field');
    const siteUrl = adapterNode.getAttribute('siteURL');
    const listId = adapterNode.getAttribute('sharePointListID');
    const relListUrl = adapterNode.getAttribute('relativeListUrl');
    const sortBy = adapterNode.getAttribute('sortBy');
    const sortAsc = hasYesAttribute('sortAscending', adapterNode);
    const fields = map(parseShPListField, fieldNodes);
    let adapter = new SharePointListAdapter(
        siteUrl,
        listId,
        relListUrl,
        fields,
        sortBy,
        sortAsc,
    );

    this.parseAdapterCommon(adapterNode, 'shpList', adapter);

    if (adapter.queryAllowed && adapter.submitAllowed) {
        adapter.type = 'shpListForm';
    }

    adapter.contentTypeID = adapterNode.getAttribute('contentTypeID');
    adapter.queryOneItemOnly = adapterNode.getAttribute('queryOneItemOnly');

    const dcExtensionNode = this.getDataConnectionExtension(
        adapter.localName,
        adapter.name,
    );
    let isQueryCurrentItem = false;

    if (dcExtensionNode) {
        isQueryCurrentItem = hasYesAttribute(
            'queryThisFormOnly',
            dcExtensionNode,
        );
    }

    adapter.isQueryCurrentItem = isQueryCurrentItem;

    return this.checkAndAddUdcxProperty(adapterNode, adapter);
};

ManifestParser.prototype.parseFileQueryAdapter = function (adapterNode) {
    var adapter = this.parseAdapterCommon(adapterNode, 'xmlFile');

    adapter.fileUrl = adapterNode.getAttribute('fileUrl');

    return adapter;
};

ManifestParser.prototype.parseWebServiceAdapter = function (adapterNode) {
    const adapter = this.parseAdapterCommon(adapterNode, 'webService');
    const operation = adapterNode.getElementsByTagNameNS(nsXsf, 'operation')[0];

    adapter.wsdlUrl = adapterNode.getAttribute('wsdlUrl');
    if (operation) {
        adapter.methodName = operation.getAttribute('name');
        adapter.soapAction = operation.getAttribute('soapAction');
        adapter.serviceUrl = operation.getAttribute('serviceUrl');
        adapter.input = parseWebServiceInput(operation);
    } else {
        // TODO: ?
    }

    return this.checkAndAddUdcxProperty(adapterNode, adapter);
};

ManifestParser.prototype.parseAdoAdapter = function (adapterNode) {
    var adapter = this.parseAdapterCommon(adapterNode, 'ado');

    adapter.connectionString = adapterNode.getAttribute('connectionString');
    adapter.commandText = adapterNode.getAttribute('commandText');

    return this.checkAndAddUdcxProperty(adapterNode, adapter);
};

ManifestParser.prototype.parseAdapterCommon = function (
    adapterNode,
    type,
    seed,
) {
    var adapter = seed || {};

    adapter.name = adapterNode.getAttribute('name');
    adapter.type = type;
    adapter.localName = adapterNode.localName;
    adapter.queryAllowed = hasYesAttribute('queryAllowed', adapterNode);
    adapter.submitAllowed = hasYesAttribute('submitAllowed', adapterNode);
    adapter.isMainQuery = this.evaluateBoolean(
        'parent::xsf:query/parent::xsf:xDocumentClass',
        { context: adapterNode },
    );

    return adapter;
};

// ======================
// End adapter parsing
// ======================

// UNUSED
ManifestParser.prototype.getOnLoadQueries = function () {
    var printStr = '';
    var onLoadQueries = [];
    var onLoadNodes = this.evaluateXPath(
        "/*/xsf:dataObjects/xsf:dataObject[@initOnLoad='yes']",
    ).toUnsortedArray();

    for (var i = 0; i < onLoadNodes.length; i += 1) {
        onLoadQueries[i] = onLoadNodes[i].getAttribute('name');
        printStr += '\n' + onLoadQueries[i];
    }
    alert('OnLoad:\n' + printStr);

    return onLoadQueries;
};

ManifestParser.prototype.getViewNode = function (viewName) {
    if (!this.viewNodes) {
        var nodes = this.evaluateXPath(
            '/*/xsf:views/xsf:view',
        ).toUnsortedArray();

        this.viewNodes = indexBy(function (n) {
            return n.getAttribute('name');
        }, nodes);
    }

    return this.viewNodes[viewName];
};

const parseContextMenuItem = function (itemNode) {
    var ga = itemNode.getAttribute.bind(itemNode);

    return new ContextMenuItem(
        ga('action'),
        ga('xmlToEdit'),
        ga('caption'),
        ga('showIf'),
    );
};

ManifestParser.prototype.extractContextMenuItems = function (viewName) {
    var viewNode = this.getViewNode(viewName);

    if (!viewNode) {
        throw Error(
            'Information on view ' +
                viewName +
                ' could not be found when parsing context menu items.',
        );
    }

    var contextItemNodes = this.evaluateXPath(
        "xsf:menuArea[@name = 'msoStructuralEditingContextMenu']/xsf:button",
        {
            context: viewNode,
        },
    );

    return groupBy(
        prop('xmlToEdit'),
        map(parseContextMenuItem, contextItemNodes.toUnsortedArray()),
    );
};

ManifestParser.prototype.extractEditableComponents = function (view) {
    // clear existing editable components
    const self = this;
    const viewNode = this.getViewNode(view);
    // get xmlToEdit nodes for the 'view'
    const xmlToEditNodes = this.evaluateXPath('xsf:editing/xsf:xmlToEdit', {
        context: viewNode,
    }).toUnsortedArray();

    return xmlToEditNodes
        .map(function (xteNode) {
            return self.parseEditableComponent(xteNode, viewNode);
        })
        .filter(function (ec) {
            return !!ec;
        });
};

function parseFileProperties(parser, fileNode) {
    return parser
        .evaluateXPath('xsf:fileProperties/xsf:property', { context: fileNode })
        .toUnsortedArray()
        .reduce(function (props, node) {
            props[node.getAttribute('name')] = node.getAttribute('value') || '';
            return props;
        }, {});
}

ManifestParser.prototype.parseAllManifestFiles = function () {
    var self = this;
    return this.evaluateXPath(
        'xsf:xDocumentClass/xsf:package/xsf:files/xsf:file',
    )
        .toUnsortedArray()
        .map(function (fileNode) {
            var props = parseFileProperties(self, fileNode);

            return {
                isResource: props.fileType === 'resource',
                name: fileNode.getAttribute('name'),
                value: fileNode.getAttribute('name'),
                properties: props,
            };
        });
};

ManifestParser.prototype.getAllManifestFiles = function () {
    if (!this.allManifestFiles) {
        this.allManifestFiles = this.parseAllManifestFiles();
    }

    return this.allManifestFiles;
};

function parseErrorCondition(errorConditionNode) {
    var errorMessage = getChildElements(
        errorConditionNode,
        'errorMessage',
        nsXsf,
    ).map(function (n) {
        return {
            type: n.getAttribute('type'),
            shortMessage: n.getAttribute('shortMessage'),
            message: n.textContent,
        };
    })[0];
    var captionNode = getChildNodes(errorConditionNode).find(
        both(isProcessingInstruction, propEq('target', 'caption')),
    );

    return {
        match: errorConditionNode.getAttribute('match'),
        expressionContext: errorConditionNode.getAttribute('expressionContext'),
        expression: errorConditionNode.getAttribute('expression'),
        errorMessage: errorMessage,
        caption: captionNode && captionNode.data,
    };
}

function extractCustomValidation(manifestParser) {
    try {
        return manifestParser
            .evaluateXPath('/*/xsf:customValidation/xsf:errorCondition')
            .toUnsortedArray()
            .map(parseErrorCondition);
    } catch (e) {
        console.error('Error parsing custom validation', e);
        return [];
    }
}

ManifestParser.prototype.getCustomValidation = function () {
    return this.lazyLoad(
        this,
        'customValidation',
        extractCustomValidation.bind(null, this),
    );
};

function getWssPath() {
    var xPath =
        '/*/xsf:extensions/xsf:extension/xsf2:solutionDefinition/xsf2:solutionPropertiesExtension/xsf2:wss/@path';

    return this.evaluateXPathToString(xPath);
}

Object.assign(ManifestParser.prototype, { getWssPath });

export default ManifestParser;
